import React, { useState, useRef } from "react";
import Row from "olive-react-myoneui/lib/Row";
import Col from "olive-react-myoneui/lib/Col";
import ModalCommon from "../ModalCommon";
import Button from "olive-react-myoneui/lib/Button";
import ProgressWrap from "olive-react-myoneui/lib/ProgressWrap";
import ProgressBar from "olive-react-myoneui/lib/ProgressBar";
import ProgressValue from "olive-react-myoneui/lib/ProgressValue";
import CircleImage from "olive-react-myoneui/lib/CircleImage";
import defaultProfileImg from "../../../../assets/img/sample.png";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
// Default Icons
import { binIcon, cameraIcon, imageIcon, uploadIcon, checkCircleComplete } from '../../../constants/icons';
import { useDispatch, useSelector } from "react-redux";
import { uploadProfileImage } from "../../../../api/afterLogin/upload";
import { setConfigData } from "../../../../reducers/configSlice";
import { Spinner } from "olive-react-myoneui";

const MAX_FILE_SIZE_MB = 3;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024; // 3MB in bytes
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png"];

const ProfileImageUploader = ({profileImage, publicProfile}) => {
  const dispatch = useDispatch();
  const configData      = useSelector((state) => state.config);
  const [showModal, setShowModal] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempImagePreview, setTempImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null); // Error state
  const [selectedFile, setSelectedFile] = useState(null);
  const [cropperVisible, setCropperVisible] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [profileImageLoading, setProfileImageLoading] = useState(false);
  const [blob, setBlob] = useState(null);
  const cropperRef = useRef(null);

  const handleImageChange = (event) => {
    setError(null); // Clear previous errors
    const file = event.target.files[0];
    if (file) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setError("Invalid file type. Only .jpg and .png files are allowed.");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`File size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setTempImagePreview(reader.result);
        setCropperImage(reader.result);
        setCropperVisible(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = () => {
    if (cropperRef.current) {
      const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();
      if (croppedCanvas) {
        croppedCanvas.toBlob((blob) => {
          if (blob) {
            setBlob(blob);
            const croppedFile = new File([blob], selectedFile.name, {
              type: selectedFile.type,
              lastModified: Date.now(),
            });
            setUploadedFile(croppedFile);
            simulateUploadProgress();

          }
        }, selectedFile.type);
      }
      setCropperVisible(false);
    }
  };

  const simulateUploadProgress = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleSave = async () => {
    setProfileImageLoading(true);
    const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();
    if (croppedCanvas) {
      setImagePreview(croppedCanvas.toDataURL());
      dispatch(setConfigData({  tmp_profile_image: croppedCanvas.toDataURL() }))
    }

    let formData = new FormData();
    formData.append("image", blob, uploadedFile.name);

    const response = await uploadProfileImage(configData.access_token, formData);
    
    setIsModalOpen(false);
    setProfileImageLoading(false);
    resetModalState();
  };

  const handleCancelUpload = () => {
    setUploadedFile(null);
    setTempImagePreview(null);
    setUploadProgress(0);
    setCropperImage(null);
    setCropperVisible(false);
    setError(null); // Clear error state
  };

  const onImageUploadClick = () => {
    setIsModalOpen(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files[0];

    if (file) {
      handleImageChange({ target: { files: [file] } });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetModalState();
  };

  const resetModalState = () => {
    setIsModalOpen(false);
    setUploadedFile(null);
    setTempImagePreview(null);
    setUploadProgress(0);
    setCropperImage(null);
    setCropperVisible(false);
    setSelectedFile(null);
    setError(null);
  };
  
  return (
    <div>
      <div className="myk_stu-avatar">
        <img
          src={imagePreview || profileImage || defaultProfileImg}
          alt="Profile Avatar"
          className="img-fluid"
        />
        {
          !publicProfile &&
          <span
            className="myk_stu-upload-btn"
            onClick={onImageUploadClick}
            role="button"
            aria-label="Upload Image"
          >
            {cameraIcon}
          </span>
        }
      </div>

      {isModalOpen && (
        <ModalCommon
          backdrop="static"
          show={showModal}
          onClose={closeModal}
          title="Upload Profile Image"
          className="myk__stu-question-modal"
          backdropOption="static"
        >
          {profileImageLoading == false ? (
            <>
              <div className="text-center">
              {!cropperVisible ? (
                <div className={`upload-container ${uploadProgress < 100 ? "" : "upload-disabled"}`} onDragOver={handleDragOver} onDrop={handleDrop} >
                  <label htmlFor="image-upload-input" className="upload-area">
                    <CircleImage className="circle-wrap circle-wrap-sm">
                      {uploadIcon}
                    </CircleImage>
                    <p>Click to upload or drag and drop</p>
                    <p>File format: .jpg, .png (max file size: {MAX_FILE_SIZE_MB}MB)</p>
                  </label>
                  <input
                    type="file"
                    id="image-upload-input"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                  />
                </div>
                ) : (
                  <Cropper
                  src={cropperImage}
                  style={{ height: 400, width: "100%" }}
                  aspectRatio={1}
                  guides={false}
                  ref={cropperRef}
                />
                )}
                {error && <p className="text-danger">{error}</p>} {/* Display errors */}
                {uploadedFile && (
                  <div className="myk__stu-upload-details">
                    <div className="information-block information-block-sm-pd bg-ghost-white mb-3 text-start">
                      <div className="information-wrap">
                        <div className="information-holder">
                          <CircleImage className="circle-wrap circle-wrap-sm bg-white text-success">
                            {imageIcon}
                          </CircleImage>
                          <div className="information-detail">
                            <h4>{uploadedFile.name} {uploadProgress < 100 ? '' : checkCircleComplete}</h4>
                            {uploadProgress < 100 ? (
                              <ProgressWrap>
                                <ProgressBar variant="primary" now={uploadProgress} />
                                <ProgressValue ProgressPercentage={`${uploadProgress}%`} />
                              </ProgressWrap>
                            ) : (
                              <div className="d-flex align-items-center gap-2">
                                <span>{(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB</span>
                                <span>- {uploadProgress}% Uploaded</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="information-white-bg"
                          className="btn btn-sm btn-round"
                          onClick={handleCancelUpload}
                        >
                          {binIcon}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Row>
                <Col className="text-end mt-3">
                  <Button variant="default" onClick={closeModal}>Cancel</Button>
                  {cropperVisible && <Button variant="primary" onClick={handleCrop}>Crop Image</Button>}
                  {!cropperVisible && <Button variant="primary" onClick={handleSave} type="submit" disabled={!tempImagePreview || uploadProgress < 100 || !selectedFile}>Save</Button>}
                </Col>
              </Row>
            </>
        ) : (
            <div className="text-center">
              <div className="myk__upload-img-loader w-100 d-flex align-items-center justify-content-center">
                <div className="d-flex align-items-center justify-content-center flex-column gap-2">
                  <Spinner animation="border" variant="secondary" />
                  <span>Uploading profile image...</span>
                </div>
              </div>
            </div>
          ) }
        </ModalCommon>
      )}
    </div>
  );
};

export default ProfileImageUploader;
